import React, { Fragment } from 'react';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import { DATA_SOURCE_TYPE, VITALS_ACTIVITY_TYPE } from '../../constants';
import { PERIOD, PROVIDER_OPTION } from '../../../common/Charts/constants';
import InfoWrapper from '../../../common/Charts/InfoWrapper';
import { mapChartData } from '../../../common/Charts/helpers';
import ModalWrapper from '../../../common/Charts/ModalWrapper';
import ChartWrapper from '../../../common/Charts/ChartWrapper';
import ChartProvider from '../../../common/Charts/ChartProvider';
import HistoryWrapper from '../../../common/Charts/HistoryWrapper';
import { DEFAULTS, TIMELINE } from '../../../common/Charts/options';
import { TOOLTIP_VERTICAL_LINE } from '../../../common/Charts/plugins';
import { dataConverter } from '../../../common/Charts/helpers/converters';
import * as bloodPressureMeasurement from '../../../../redux/profiles/patientHealthboard/bloodPressure-measurements-req';
import * as bloodPressureMeasurementModal from '../../../../redux/profiles/patientHealthboard/bloodPressure-measurements-modal-req';
import {
  useChartActions,
  useChartFormats,
  useHistoryActions,
  useSafeDataSourceTitle,
} from '../../../common/Charts/hooks';
import {
  selectHistoricMeasurementsList,
  selectBloodPressureMeasurementEntity,
  selectHistoricMeasurementsListPending,
  selectBloodPressureMeasurementModalEntity,
  selectBloodPressureMeasurementEntityPending,
  selectBloodPressureMeasurementModalEntityPending,
} from '../../../../redux/profiles/patientHealthboard/selectors';
import {
  calculateMinMaxRangeForRawValue,
  getOriginalProviderCode,
} from '../../utils';
import { getCustomOptions } from './options';
import { useCalculatePaddingYAxis } from '../useCalculatePaddingYAxis';

const { DAY, WEEK, MONTH } = PERIOD;
const { BLOOD_PRESSURE } = VITALS_ACTIVITY_TYPE;
const { NAME } = PROVIDER_OPTION;

const BloodPressureChart = ({ intl: { formatMessage } }) => {
  const historyData = useSelector(selectHistoricMeasurementsList);
  const periodData = useSelector(selectBloodPressureMeasurementEntity);
  const modalData = useSelector(selectBloodPressureMeasurementModalEntity);
  const historyLoading = useSelector(selectHistoricMeasurementsListPending);
  const periodLoading = useSelector(
    selectBloodPressureMeasurementEntityPending,
  );
  const modalLoading = useSelector(
    selectBloodPressureMeasurementModalEntityPending,
  );

  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  const formats = useChartFormats({ formatMessage });

  const { loadHistory } = useHistoryActions({ type: BLOOD_PRESSURE });

  const { isModal, openDay, closeDay, baseDate, loadPeriod } = useChartActions({
    actions: bloodPressureMeasurement.actions,
    modalActions: bloodPressureMeasurementModal.actions,
  });
  const metric = formatMessage({
    id: 'patient.healthboard.blood_pressure.metric',
  });

  const paddingYAxisTicks = useCalculatePaddingYAxis();

  const setConfig = (data, date) => ({ period }) => {
    const { min, max } = calculateMinMaxRangeForRawValue(data);
    return {
      type: 'line',
      plugins: [TOOLTIP_VERTICAL_LINE],
      data: mapChartData(
        data,
        getCustomOptions({ period, date, formats, metric }),
      ),
      options: merge(DEFAULTS, TIMELINE[period], {
        layout: {
          padding: {
            top: isModal ? 120 : 140,
          },
        },
        scales: {
          yAxes: [
            {
              ticks: {
                min,
                max,
                autoSkip: true,
                autoSkipPadding: paddingYAxisTicks,
                callback(value, _, allData) {
                  const roundedValue = Math.round(value);
                  if (allData.includes(roundedValue) && roundedValue !== value)
                    return null;
                  return roundedValue;
                },
              },
            },
          ],
        },
        tooltips: {
          mode: 'point',
          intersect: true,
          enabled: [DAY, WEEK, MONTH].includes(period),
          callbacks: {
            ...DEFAULTS.tooltips,
            footer(points, { datasets, customOptions }) {
              const { providers } = customOptions;
              const { datasetIndex } = points[0];
              const dataset = datasetIndex
                ? datasets[datasetIndex]
                : datasets[0];
              const providerCode = dataset.label.split('.')[0];
              const codesDataSet = datasets.filter(item =>
                item.label.includes(providerCode),
              );
              if (providerCode === DATA_SOURCE_TYPE.DEVICES) {
                const label = dataset.label.split('.')[1];
                const devicesPeriodData = (isModal
                  ? modalData
                  : periodData
                ).find(el => el.provider.code === DATA_SOURCE_TYPE.DEVICES);
                const [f] = points;
                const { index } = f;
                if (codesDataSet.length > 1) {
                  const [first, second] = codesDataSet;
                  const tooltipData = devicesPeriodData.measurements.find(
                    el => {
                      if (label === 'bpDiastolic') {
                        return el.value.bpDiastolic === second.data[index];
                      }
                      return el.value.bpSystolic === first.data[index];
                    },
                  );
                  if (tooltipData) {
                    return getSafeDataSourceTitle(
                      getOriginalProviderCode(
                        tooltipData.originalProviderCodes,
                      ),
                      formatMessage,
                    );
                  }
                }
              }
              return providers[providerCode][NAME];
            },
          },
        },
        spanGaps: true,
      }),
    };
  };

  const context = {
    isModal,
    mark: metric,
    converter: dataConverter.minmax,
    range: formatMessage({ id: 'patient.healthboard.minMax' }),
  };

  return (
    <Fragment>
      <ChartProvider extend={context}>
        <ChartWrapper
          onLoad={loadPeriod}
          loading={periodLoading}
          setConfig={setConfig(periodData)}
        />

        <InfoWrapper
          subtitle={formatMessage({ id: 'patient.healthboard.minMaxData' })}
          title={formatMessage({
            id: 'patient.healthboard.titleHistoric.all.blood_pressure',
          })}
        >
          <HistoryWrapper
            unit="day"
            onClick={openDay}
            data={historyData}
            onLoad={loadHistory}
            loading={historyLoading}
          />
        </InfoWrapper>
      </ChartProvider>

      <ModalWrapper
        title={formatMessage({
          id: 'patient.healthboard.titleHistoric.blood_pressure',
        })}
        open={isModal}
        onClose={closeDay}
      >
        <ChartProvider extend={context}>
          <ChartWrapper
            isModal
            loading={modalLoading}
            setConfig={setConfig(modalData, baseDate)}
            footer={<HistoryWrapper isModal data={modalData} />}
          />
        </ChartProvider>
      </ModalWrapper>
    </Fragment>
  );
};

BloodPressureChart.propTypes = {
  intl: PropTypes.object.isRequired,
};

export default injectIntl(BloodPressureChart);



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/TimeScalableChart/BloodPressureChart/index.jsx